import React, { useRef, useEffect } from 'react';
import { FaDesktop, FaWindowMaximize } from 'react-icons/fa';
import { useI18n } from '../../i18n';

const SnapSubmenu = ({ isOpen, onClose, position }) => {
    const { t } = useI18n();
    const submenuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (submenuRef.current && !submenuRef.current.contains(event.target)) {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    // Shared download function
    const downloadImage = (canvas, filename) => {
        canvas.toBlob((blob) => {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }, 'image/png');
    };

    const takeFullScreenSnap = async () => {
        try {
            const { default: html2canvas } = await import('html2canvas');

            // Store original scroll position
            const originalScrollX = window.scrollX;
            const originalScrollY = window.scrollY;

            // Get the main content area (excluding sidebar)
            const mainContent = document.querySelector('.dashboard-content') || document.body;

            // Capture the entire scrollable content
            const canvas = await html2canvas(mainContent, {
                allowTaint: true,
                useCORS: true,
                scrollX: 0,
                scrollY: 0,
                windowWidth: mainContent.scrollWidth,
                windowHeight: mainContent.scrollHeight,
                width: mainContent.scrollWidth,
                height: mainContent.scrollHeight,
                scale: 1,
                logging: false
            });

            // Restore scroll position
            window.scrollTo(originalScrollX, originalScrollY);

            downloadImage(canvas, `full-screen-snap-${Date.now()}.png`);
            onClose();

        } catch (error) {
            console.error('Error taking full screen screenshot:', error);
            alert(t('errors.full_screen_capture_failed'));
            onClose();
        }
    };

    const takeViewportSnap = async () => {
        try {
            const { default: html2canvas } = await import('html2canvas');

            // Get the main content area (excluding sidebar)
            const mainContent = document.querySelector('.dashboard-content') || document.body;

            // Capture only the visible viewport
            const canvas = await html2canvas(mainContent, {
                allowTaint: true,
                useCORS: true,
                width: window.innerWidth,
                height: window.innerHeight,
                scrollX: window.scrollX,
                scrollY: window.scrollY,
                scale: 1,
                logging: false
            });

            downloadImage(canvas, `viewport-snap-${Date.now()}.png`);
            onClose();

        } catch (error) {
            console.error('Error taking viewport screenshot:', error);
            alert(t('errors.viewport_capture_failed'));
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div
            ref={submenuRef}
            className="snap-submenu"
            style={{
                position: 'absolute',
                left: `${position.x + 90}px`, // Position to the right of sidebar
                top: `${position.y}px`,
                zIndex: 1000,
                backgroundColor: '#0C5097',
                borderRadius: '8px',
                padding: '8px 0',
                minWidth: '200px',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
        >
            <div
                className="snap-submenu-item ui-font-14"
                onClick={takeFullScreenSnap}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    color: '#ffffff',
                    transition: 'background-color 0.2s ease',
                    fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                }}
            >
                <FaDesktop style={{ marginRight: '12px', fontSize: '16px' }} />
                {t('pages.snap_submenu.take_full_screen')}
            </div>

            <div
                className="snap-submenu-item ui-font-14"
                onClick={takeViewportSnap}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    cursor: 'pointer',
                    color: '#ffffff',
                    transition: 'background-color 0.2s ease',
                    fontWeight: '500'
                }}
                onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.15)';
                }}
                onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                }}
            >
                <FaWindowMaximize style={{ marginRight: '12px', fontSize: '16px' }} />
                {t('pages.snap_submenu.take_current_screen')}
            </div>
        </div>
    );
};

export default SnapSubmenu;
